/**
 * « Continuer avec Google » côté navigateur : démarrage de la redirection (PKCE) et état du fournisseur.
 *
 * Une console crée UNE connexion (`creerConnexionGoogle`) avec SON client d'authentification, l'adresse de son API et la
 * clé publique qu'elle embarque déjà, puis la passe à `<BoutonGoogle connexion=… />`.
 *
 * Le client DOIT avoir été créé avec `flowType: 'pkce'` et `detectSessionInUrl` actif : le retour arrive avec `?code=…`
 * (aucun jeton dans l'URL), échangé contre une session au démarrage. Se connecter ne donne AUCUN droit : voir `acces.ts`.
 */
import {
  MESSAGE_GOOGLE_ECHEC,
  MESSAGE_GOOGLE_INACTIF,
  MESSAGE_GOOGLE_RESEAU,
} from './retour'
import { lireFournisseurActif } from './reglages'

/** Le strict nécessaire du client d'authentification (le vrai client de la bibliothèque le satisfait). */
export interface ClientOAuth {
  auth: {
    signInWithOAuth(credentials: {
      provider: 'google'
      options?: { redirectTo?: string; queryParams?: Record<string, string> }
    }): Promise<{ error: { message: string } | null }>
  }
}

export interface ConnexionGoogle {
  /** `true` / `false` = déclaré par le serveur ; `null` = inconnu. Lu une fois par page quand la réponse est sûre. */
  fournisseurActif(): Promise<boolean | null>
  /** Redirige le navigateur vers Google. Ne retourne que si cela a échoué : l'échec est une exception à message lisible. */
  continuer(): Promise<void>
}

/** Adresse de retour par défaut : la console telle que le navigateur la voit (origine + chemin, sans requête ni fragment). */
export function urlDeRetourParDefaut(loc: { origin: string; pathname: string }): string {
  return `${loc.origin}${loc.pathname}`
}

export function phraseErreurDemarrage(message: string): string {
  if (/not enabled|unsupported provider/i.test(message)) return MESSAGE_GOOGLE_INACTIF
  return MESSAGE_GOOGLE_ECHEC
}

export function creerConnexionGoogle(p: {
  client: ClientOAuth
  url: string
  cle: string
  /** Adresse de retour (doit figurer dans la liste des retours autorisés du serveur). Défaut : origine + chemin. */
  urlRetour?: () => string
  fetchImpl?: typeof fetch
}): ConnexionGoogle {
  let connu: boolean | null = null
  const fournisseurActif = async (): Promise<boolean | null> => {
    if (connu !== null) return connu
    const r = await lireFournisseurActif({ url: p.url, cle: p.cle, ...(p.fetchImpl ? { fetchImpl: p.fetchImpl } : {}) })
    /* Une réponse sûre est gardée ; « inconnu » (réseau) est réessayé au prochain affichage. */
    if (r !== null) connu = r
    return r
  }
  return {
    fournisseurActif,
    async continuer() {
      const actif = await fournisseurActif()
      if (actif === false) throw new Error(MESSAGE_GOOGLE_INACTIF)
      if (actif === null) throw new Error(MESSAGE_GOOGLE_RESEAU)
      const redirectTo = (p.urlRetour ?? (() => urlDeRetourParDefaut(window.location)))()
      /* `select_account` : Google propose toujours le choix du compte, même avec un seul compte connecté dans le navigateur
         (poste partagé, plusieurs adresses). */
      const { error } = await p.client.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo, queryParams: { prompt: 'select_account' } },
      })
      if (error) throw new Error(phraseErreurDemarrage(error.message))
    },
  }
}
